import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';
import { ajax } from 'cadenza/api-client/api';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { CuratedMapViewDto, CuratedMapViewListingDto } from 'cadenza/api-client/model/curated-map-view-dto';
import type { TableData, TableParams } from 'cadenza/api-client/tables-api';
import { tableParams } from 'cadenza/api-client/tables-api';
import type { CuratedMapViewId } from 'cadenza/repository/model/curated-map-view-id';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';

export type UpdateCuratedMapViewDto = Partial<Pick<CuratedMapViewDto, 'printName' | 'description'>>;
type SaveCuratedMapViewDto = {
  workbookNameAndHash: WorkbookNameAndHash;
  viewId: ViewId;
  mapExtent?: WorldBoxDto;
} & UpdateCuratedMapViewDto;

/** @see net/disy/cadenza/mapcatalog/web/dto/CuratedMapViewSettingsDto.java */
export interface CuratedMapViewSettingsDto {
  defaultMap?: CuratedMapViewId;
}

/** @see net/disy/cadenza/mapcatalog/web/dto/DefaultMapValidity.java */
type DefaultMapValidity = 'VALID' | 'MISSING' | 'NOT_DEFINED' | 'NONE';

/** @see net/disy/cadenza/mapcatalog/web/dto/DefaultMapSettingsDto.java */
export interface DefaultMapSettingsDto {
  itemId?: CuratedMapViewId;
  printName?: string;
  validity: DefaultMapValidity;
}

export function callSaveAsCuratedMapView(repositoryName: RepositoryName, data: SaveCuratedMapViewDto) {
  return ajax(`/repositories/${repositoryName}/curated-map-views`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    dataType: 'json',
    data: JSON.stringify(data),
  });
}

export function replaceCuratedMapView({ repositoryName, itemName }: CuratedMapViewId, data: SaveCuratedMapViewDto) {
  return ajax(`/repositories/${repositoryName}/curated-map-views/${itemName}`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    dataType: 'json',
    data: JSON.stringify(data),
  });
}

export function updateCuratedMapView({ repositoryName, itemName }: CuratedMapViewId, data: UpdateCuratedMapViewDto) {
  return ajax<CuratedMapViewDto>(`/repositories/${repositoryName}/curated-map-views/${itemName}`, {
    method: 'PATCH',
    contentType: MIMETYPE_JSON,
    dataType: 'json',
    data: JSON.stringify(data),
  });
}

export function deleteCuratedMapView({ repositoryName, itemName }: CuratedMapViewId) {
  return ajax<CuratedMapViewDto>(`/repositories/${repositoryName}/curated-map-views/${itemName}`, {
    contentType: MIMETYPE_JSON,
    method: 'DELETE',
  });
}

export function getAllCuratedMapViews({
  privilege,
  ...params
}: TableParams & {
  privilege?: ObjectPrivilege;
} = {}): Promise<TableData<CuratedMapViewListingDto>> {
  const urlParams = tableParams(params);
  if (privilege) {
    urlParams.append('privilege', privilege);
  }
  return ajax<TableData<CuratedMapViewListingDto>>(`/curated-map-views?${urlParams}`).then((tableData) => ({
    ...tableData,
    rows: tableData.rows.map((rawDto) => ({
      ...rawDto,
      creationDate: new Date(rawDto.creationDate),
    })),
  }));
}

export function getCuratedDefaultMap() {
  return ajax<DefaultMapSettingsDto>('/config/default-map');
}

export function setCuratedDefaultMap(settings: CuratedMapViewSettingsDto) {
  return ajax<DefaultMapSettingsDto>('/config/default-map', {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(settings),
  });
}

export function getRepositoriesContainingCuratedMapViews(): Promise<{ name: RepositoryName; printName: string }[]> {
  return ajax('/repositories-with-curated-map-views');
}

export async function isDefaultClassicMapDefined() {
  try {
    await ajax('/classic-default-map', {
      method: 'HEAD',
      data: { width: 10, height: 10 }, // Parameters needed in endpoint, otherwise 400 Bad request will be returned
    });
    return true;
  } catch (error: unknown) {
    const response = error as { status: number };
    if (response.status === 404) {
      return false;
    } else {
      throw error;
    }
  }
}
